import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { FolderDialogState, FolderState, LoadRequestState, UserFoldersState } from './slice'
import { FolderResponse } from '../schemas'
import { isActiveExternalStorageItem, isTombstonedExternalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { type DriveListSort, kDefaultDriveListSort } from '@/user_files/sorting'

const EMPTY_FOLDER_IDS: string[] = []
const EMPTY_FOLDERS: FolderResponse[] = []
const IDLE_LOAD_REQUEST_STATE: LoadRequestState = { status: 'idle', error: null }

/**
 * Actively synced folders live on the External Files tab, not the Drive.
 * Tombstoned folders (sync disconnected) count as regular Drive folders.
 */
export function isExternalStorageSyncedFolder(folder: Pick<FolderResponse, 'external_storage'>): boolean {
  return isActiveExternalStorageItem(folder)
}

const selectShouldShowExternalStorageItemsInDrive = (_state: RootState): boolean => kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SHOW_EXTERNAL_STORAGE_ITEMS)

// ============================================================================
// Base Selectors (raw state access)
// ============================================================================

export const selectUserFoldersState = (state: RootState): UserFoldersState => state.userFoldersState

export const selectFoldersRecord = (state: RootState): Record<string, FolderState> => state.userFoldersState.folders

export const selectChildrenByParentId = (state: RootState): Record<string, string[]> => state.userFoldersState.childrenByParentId

export const selectRootByMatterId = (state: RootState): Record<string, string> => state.userFoldersState.rootByMatterId

export const selectRootFolderRequestsByMatterId = (state: RootState): Record<string, LoadRequestState> => state.userFoldersState.rootFolderRequestsByMatterId

export const selectCurrentFolderId = (state: RootState): string | null => state.userFoldersState.currentFolderId

/** The sort this matter's drive is displayed under, or the API default if it has not been sorted. */
export const selectDriveListSortForMatter = (state: RootState, props: { matterId: string | null | undefined }): DriveListSort => {
  if (!props.matterId) return kDefaultDriveListSort
  return state.userFoldersState.driveListSortByMatterId[props.matterId] ?? kDefaultDriveListSort
}

export const selectActiveRequests = (state: RootState) => state.userFoldersState.activeRequests

export const selectInitialLoadComplete = (state: RootState): boolean => state.userFoldersState.initialLoadComplete

export const selectIsInitializingFromUrl = (state: RootState): boolean => state.userFoldersState.isInitializingFromUrl

export const selectUrlInitializationError = (state: RootState) => state.userFoldersState.urlInitializationError

// ============================================================================
// Current Folder Selectors (Navigation)
// ============================================================================

/**
 * Select current folder state (data + load status)
 */
export const selectCurrentFolderState = createSelector([selectFoldersRecord, selectCurrentFolderId], (folders, currentId): FolderState | null => {
  if (!currentId) return null
  return folders[currentId] ?? null
})

/**
 * Select current folder data (just the backend data)
 */
export const selectCurrentFolder = createSelector([selectCurrentFolderState], (folderState): FolderResponse | null => folderState?.data ?? null)

/**
 * Select current folder name
 */
export const selectCurrentFolderName = createSelector([selectCurrentFolder], (folder): string | null => folder?.name ?? null)

/**
 * Select if current folder is root
 */
export const selectIsCurrentFolderRoot = createSelector([selectCurrentFolder], (folder): boolean => folder?.is_root ?? false)

/**
 * Select if current folder's children are loaded
 */
export const selectAreCurrentFolderChildrenLoaded = createSelector([selectCurrentFolderState], (folderState): boolean => folderState?.childrenLoaded ?? false)

// ============================================================================
// Children of Current Folder Selectors
// ============================================================================

/**
 * Select child folder IDs for current folder
 */
export const selectCurrentFolderChildIds = createSelector([selectChildrenByParentId, selectCurrentFolderId], (childrenByParent, currentId): string[] => {
  if (!currentId) return []
  return childrenByParent[currentId] ?? []
})

/**
 * Select child folders for current folder, in the order the drive requested from the server.
 *
 * Deliberately unsorted here: the drive's sortable header sends `sort_by`/`sort_order` to the list
 * endpoint, so `childrenByParentId` already holds the ordering the user asked for and re-sorting it
 * client-side would override every sort but Name ascending. Consumers with no sorting UI use
 * `selectChildFolders`, which keeps its own alphabetical ordering.
 */
export const selectCurrentFolderChildren = createSelector([selectFoldersRecord, selectCurrentFolderChildIds], (folders, childIds): FolderResponse[] => {
  return childIds.map((id) => folders[id]?.data).filter((f): f is FolderResponse => f !== undefined)
})

/**
 * Drive-visible child folders for the current folder.
 * Hides only actively synced folders; tombstoned (disconnected) folders stay visible.
 * Raw selectors intentionally keep synced folders for pickers and other consumers.
 */
export const selectCurrentFolderDriveVisibleChildren = createSelector(
  [selectCurrentFolderChildren, selectShouldShowExternalStorageItemsInDrive],
  (folders, showExternalStorageItems): FolderResponse[] => {
    if (showExternalStorageItems) return folders
    return folders.filter((folder) => !isExternalStorageSyncedFolder(folder))
  }
)

/**
 * Select child folder count for current folder
 */
export const selectCurrentFolderChildCount = createSelector([selectCurrentFolderChildIds], (childIds): number => childIds.length)

/**
 * Select if current folder has children
 */
export const selectCurrentFolderHasChildren = createSelector([selectCurrentFolderChildCount], (count): boolean => count > 0)

// ============================================================================
// Folder by ID Selectors (parameterized)
// ============================================================================

/**
 * Select folder state by ID
 */
export const selectFolderById = (state: RootState, props: { folderId: string | null | undefined }): FolderState | null => {
  if (!props.folderId) return null
  return state.userFoldersState.folders[props.folderId] ?? null
}

/**
 * Select folder data by ID (just the backend data)
 */
export const selectFolderDataById = (state: RootState, props: { folderId: string | null | undefined }): FolderResponse | null => {
  const folderState = selectFolderById(state, props)
  return folderState?.data ?? null
}

/**
 * Select folder name by ID
 */
export const selectFolderNameById = (state: RootState, props: { folderId: string | null | undefined }): string | null => {
  const folder = selectFolderDataById(state, props)
  return folder?.name ?? null
}

/**
 * Select if folder is root by ID
 */
export const selectIsFolderRoot = (state: RootState, props: { folderId: string | null | undefined }): boolean => {
  const folder = selectFolderDataById(state, props)
  return folder?.is_root ?? false
}

/**
 * Select child folder IDs for a specific parent
 */
export const selectChildFolderIds = (state: RootState, props: { parentId: string | null | undefined }): string[] => {
  if (!props.parentId) return EMPTY_FOLDER_IDS
  return state.userFoldersState.childrenByParentId[props.parentId] ?? EMPTY_FOLDER_IDS
}

/**
 * Select child folders for a specific parent (full folder data), always alphabetically.
 *
 * The alphabetical sort is load-bearing, not decorative: `childrenByParentId` is shared with the
 * drive, which fetches it in whatever order its header is set to. The file picker and move dialog
 * read through here and have no sorting UI, so they normalise the order themselves.
 */
export const selectChildFolders = createSelector([selectFoldersRecord, selectChildFolderIds], (folders, childIds): FolderResponse[] => {
  if (childIds.length === 0) return EMPTY_FOLDERS
  return childIds
    .map((id) => folders[id]?.data)
    .filter((f): f is FolderResponse => f !== undefined)
    .sort((a, b) => a.name.localeCompare(b.name))
})

/** Preserves API sort order; selectChildFolders sorts alphabetically for pickers. */
export const selectChildFoldersInFetchedOrder = createSelector([selectFoldersRecord, selectChildFolderIds], (folders, childIds): FolderResponse[] => {
  if (childIds.length === 0) return EMPTY_FOLDERS
  return childIds.map((id) => folders[id]?.data).filter((f): f is FolderResponse => f !== undefined)
})

export const selectChildFoldersRequest = (state: RootState, props: { parentId: string | null | undefined }): LoadRequestState => {
  if (!props.parentId) return IDLE_LOAD_REQUEST_STATE
  return state.userFoldersState.folders[props.parentId]?.childrenRequest ?? IDLE_LOAD_REQUEST_STATE
}

// ============================================================================
// Matter-based Selectors
// ============================================================================

/**
 * Select root folder ID for a specific matter
 */
export const selectRootFolderIdForMatter = (state: RootState, props: { matterId: string | null | undefined }): string | null => {
  if (!props.matterId) return null
  return state.userFoldersState.rootByMatterId[props.matterId] ?? null
}

/**
 * Select root folder for a specific matter
 */
export const selectRootFolderForMatter = (state: RootState, props: { matterId: string | null | undefined }): FolderResponse | null => {
  const rootId = selectRootFolderIdForMatter(state, props)
  if (!rootId) return null
  return state.userFoldersState.folders[rootId]?.data ?? null
}

/**
 * Select if root folder is loaded for a specific matter
 */
export const selectIsRootFolderLoaded = (state: RootState, props: { matterId: string | null | undefined }): boolean => {
  return selectRootFolderIdForMatter(state, props) !== null
}

export const selectRootFolderRequestForMatter = (state: RootState, props: { matterId: string | null | undefined }): LoadRequestState => {
  if (!props.matterId) return IDLE_LOAD_REQUEST_STATE
  return state.userFoldersState.rootFolderRequestsByMatterId[props.matterId] ?? IDLE_LOAD_REQUEST_STATE
}

/**
 * Select root folder ID for the active matter (from mattersState)
 */
export const selectActiveRootFolderId = (state: RootState): string | null => {
  const activeMatterId = state.mattersState.activeMatterId
  if (!activeMatterId) return null
  return state.userFoldersState.rootByMatterId[activeMatterId] ?? null
}

/**
 * Select root folder for the active matter
 */
export const selectActiveRootFolder = createSelector([selectFoldersRecord, selectActiveRootFolderId], (folders, rootId): FolderResponse | null => {
  if (!rootId) return null
  return folders[rootId]?.data ?? null
})

// ============================================================================
// Breadcrumb / Path Selectors
// ============================================================================

/**
 * Select ancestor folder IDs from root to current folder (for breadcrumbs)
 */
export const selectCurrentFolderAncestorIds = createSelector([selectFoldersRecord, selectCurrentFolderId], (folders, currentId): string[] => {
  if (!currentId) return []

  const ancestors: string[] = []
  let id: string | null = currentId

  // Walk up the tree, collecting ancestors (leaf to root order)
  while (id) {
    const folderState: FolderState | undefined = folders[id]
    if (!folderState) break

    ancestors.push(id)
    id = folderState.data.parent_id
  }

  return ancestors.reverse()
})

/**
 * Select ancestor folders (full data) for breadcrumbs
 */
export const selectCurrentFolderBreadcrumbs = createSelector(
  [selectFoldersRecord, selectCurrentFolderAncestorIds],
  (folders, ancestorIds): FolderResponse[] => {
    return ancestorIds.map((id) => folders[id]?.data).filter((f): f is FolderResponse => f !== undefined)
  }
)

/**
 * Current folder is a tombstoned (sync-disconnected) provider folder. Uploads and
 * folder creation inside it are rejected by the backend external-storage mutation
 * guard, so the drive UI hides those entry points.
 */
export const selectIsCurrentFolderTombstoned = createSelector([selectCurrentFolder], (folder): boolean =>
  folder ? isTombstonedExternalStorageItem(folder) : false
)

export const selectIsCurrentFolderDriveHidden = createSelector(
  [selectCurrentFolder, selectShouldShowExternalStorageItemsInDrive],
  (folder, showExternalStorageItems): boolean => {
    if (showExternalStorageItems) return false
    return folder ? isExternalStorageSyncedFolder(folder) : false
  }
)

// ============================================================================
// Request State Selectors
// ============================================================================

/**
 * Select if any request is pending for a specific folder
 */
export const selectIsFolderOperationPending = (state: RootState, props: { folderId: string }): boolean => {
  return Object.values(state.userFoldersState.activeRequests).some((req) => req.folderId === props.folderId && req.status === 'pending')
}

/**
 * Select if a fetch-by-id request is pending for a specific folder
 */
export const selectFolderFetchPending = (state: RootState, props: { folderId: string | null | undefined }): boolean => {
  if (!props.folderId) return false
  return Object.values(state.userFoldersState.activeRequests).some(
    (req) => req.folderId === props.folderId && req.operationType === 'fetchFolder' && req.status === 'pending'
  )
}

/**
 * Select whether a children listing is being loaded for a folder.
 *
 * Read from `activeRequests` rather than `childrenRequest`, which `invalidateFolderChildren` resets
 * to idle while the request itself is still running, so it would under-report a load in flight.
 */
export const selectFolderChildrenFetchPending = (state: RootState, props: { folderId: string | null | undefined }): boolean => {
  if (!props.folderId) return false
  return Object.values(state.userFoldersState.activeRequests).some(
    (req) => req.folderId === props.folderId && req.operationType === 'fetchChildren' && req.status === 'pending'
  )
}

/**
 * Select whether a fetch-by-id request for a folder failed.
 * `requestFailed` leaves errored requests in `activeRequests`; callers that only care about the
 * current open/request should combine this with their own attempt tracking.
 */
export const selectFolderFetchFailed = (state: RootState, props: { folderId: string | null | undefined }): boolean => {
  if (!props.folderId) return false
  return Object.values(state.userFoldersState.activeRequests).some(
    (req) => req.folderId === props.folderId && req.operationType === 'fetchFolder' && req.status === 'error'
  )
}

/**
 * Select if any global operation is pending
 */
export const selectIsAnyOperationPending = createSelector([selectActiveRequests], (requests): boolean =>
  Object.values(requests).some((req) => req.status === 'pending')
)

// ============================================================================
// Dialog Selectors
// ============================================================================

/**
 * Select dialog state
 */
export const selectFolderDialog = (state: RootState): FolderDialogState => state.userFoldersState.dialog

/**
 * Select dialog type
 */
export const selectFolderDialogType = (state: RootState) => state.userFoldersState.dialog.dialogType

/**
 * Select dialog folder ID (for rename)
 */
export const selectFolderDialogFolderId = (state: RootState) => state.userFoldersState.dialog.folderId

/**
 * Select folder data for current dialog (for rename)
 */
export const selectFolderDialogFolder = createSelector([selectFoldersRecord, selectFolderDialogFolderId], (folders, folderId): FolderResponse | null => {
  if (!folderId) return null
  return folders[folderId]?.data ?? null
})
